import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from './AdminLayout';
import {
  ArrowLeft, Upload, Download, CheckCircle2, AlertCircle,
  Loader2, FileSpreadsheet, X, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PREVIEW_COLUMNS = ['title', 'category', 'level', 'price', 'installment_enabled', 'awarding_body', 'active'];

export default function AdminCourseBulkUpload() {
  const { authHeaders } = useAdminAuth();
  const fileInput = useRef(null);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [parseError, setParseError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadError, setUploadError] = useState('');

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setFile(f);
    setResult(null);
    setUploadError('');
    setParseError('');
    setPreview([]);

    const name = f.name.toLowerCase();
    if (name.endsWith('.csv')) {
      try {
        const text = await f.text();
        const rows = parseCsvPreview(text);
        setPreview(rows);
      } catch (err) {
        setParseError('Could not parse CSV. Make sure it matches the template format.');
      }
    } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      // For Excel we can't parse client-side without a library — just show file name
      setPreview([]);
    } else {
      setParseError('Unsupported file type. Upload a .csv or .xlsx file.');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadError('');
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/admin/courses/bulk-upload`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (err) {
      setUploadError(err?.response?.data?.detail || 'Upload failed. Check the file and try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/courses/bulk-upload/template`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'course_upload_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Could not download template.');
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview([]);
    setParseError('');
    setResult(null);
    setUploadError('');
    if (fileInput.current) fileInput.current.value = '';
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link to="/admin/courses" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Bulk Upload Courses</h1>
            <p className="text-gray-500 text-sm">Import multiple courses from a CSV or Excel file</p>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
          <Info size={16} className="text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 space-y-1">
            <p className="font-medium">How it works</p>
            <ul className="list-disc list-inside space-y-0.5 text-blue-700">
              <li>Download the template, fill in your courses, then upload.</li>
              <li>Modules column: separate multiple modules with a pipe <code className="bg-blue-100 px-1 rounded">|</code> character.</li>
              <li>If a course slug already exists, its data will be <strong>updated</strong>. New slugs are inserted.</li>
              <li>Leave <strong>slug</strong> blank to auto-generate from the title.</li>
              <li>Set <strong>installment_enabled</strong> to TRUE to offer 2-instalment option (+£100 on total).</li>
            </ul>
          </div>
        </div>

        {/* Download template */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-medium text-gray-900 text-sm">Download Template</p>
            <p className="text-xs text-gray-500 mt-0.5">CSV with all required columns and an example row</p>
          </div>
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Download size={15} /> course_upload_template.csv
          </button>
        </div>

        {/* Upload area */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-semibold text-gray-900 text-sm border-b border-gray-100 pb-2">Upload File</h2>

          {!file ? (
            <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-gray-200 rounded-xl py-10 cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors">
              <FileSpreadsheet size={32} className="text-gray-300" />
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700">Drop your file here or click to browse</p>
                <p className="text-xs text-gray-400 mt-1">Accepts .csv and .xlsx</p>
              </div>
              <input
                ref={fileInput}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          ) : (
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
              <FileSpreadsheet size={20} className="text-blue-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <button onClick={clearFile} className="text-gray-400 hover:text-red-500 transition-colors">
                <X size={16} />
              </button>
            </div>
          )}

          {parseError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2.5 text-sm">
              <AlertCircle size={14} /> {parseError}
            </div>
          )}
        </div>

        {/* CSV Preview */}
        {preview.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 text-sm">Preview ({preview.length} rows)</h2>
              <span className="text-xs text-gray-400">Showing key columns only</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-500 uppercase tracking-wide">#</th>
                    {PREVIEW_COLUMNS.map(col => (
                      <th key={col} className="text-left px-3 py-2 font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        {col.replace('_', ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {preview.slice(0, 20).map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50/50'}>
                      <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                      {PREVIEW_COLUMNS.map(col => (
                        <td key={col} className="px-3 py-2 text-gray-700 max-w-[200px] truncate" title={row[col]}>
                          {col === 'installment_enabled' ? (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              (row[col] || '').toUpperCase() === 'TRUE'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {(row[col] || '').toUpperCase() === 'TRUE' ? 'Yes' : 'No'}
                            </span>
                          ) : col === 'price' ? (
                            row[col] ? `£${row[col]}` : '—'
                          ) : (
                            row[col] || <span className="text-gray-300">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.length > 20 && (
                <p className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
                  Showing first 20 of {preview.length} rows.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Upload button */}
        {file && !parseError && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleUpload}
              disabled={uploading || !!result}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-colors"
            >
              {uploading
                ? <><Loader2 size={15} className="animate-spin" /> Uploading…</>
                : <><Upload size={15} /> Upload &amp; Import</>}
            </button>
            {result && (
              <Link
                to="/admin/courses"
                className="text-sm text-blue-600 hover:underline font-medium"
              >
                View all courses →
              </Link>
            )}
          </div>
        )}

        {/* Upload error */}
        {uploadError && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={15} /> {uploadError}
          </div>
        )}

        {/* Result summary */}
        {result && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="font-semibold text-gray-900 text-sm border-b border-gray-100 pb-2 flex items-center gap-2">
              <CheckCircle2 size={15} className="text-green-600" /> Import Complete
            </h2>
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Rows processed" value={result.total_rows} color="gray" />
              <StatCard label="Created" value={result.created} color="green" />
              <StatCard label="Updated" value={result.updated} color="blue" />
            </div>

            {result.errors?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-red-700 mb-2">
                  {result.errors.length} row{result.errors.length > 1 ? 's' : ''} skipped due to errors:
                </p>
                <div className="space-y-1">
                  {result.errors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                      <AlertCircle size={12} className="shrink-0 mt-0.5" />
                      <span><strong>Row {err.row}:</strong> {err.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <div className={`rounded-lg border p-3 text-center ${colors[color]}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs mt-0.5 opacity-75">{label}</p>
    </div>
  );
}

function parseCsvPreview(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = splitCsvLine(lines[0]);
  return lines.slice(1).map(line => {
    const vals = splitCsvLine(line);
    return Object.fromEntries(headers.map((h, i) => [h.trim(), (vals[i] || '').trim()]));
  });
}

function splitCsvLine(line) {
  const result = [];
  let inQuote = false;
  let cur = '';
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuote = !inQuote;
    } else if (ch === ',' && !inQuote) {
      result.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  result.push(cur);
  return result;
}
